import React, { useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import moment from "moment";
import PropTypes from "prop-types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMediaQuery } from "@mui/material";

import BaseTable from "Components/Common/BaseTable";
import FilterApplicationRequests from "Components/Common/FilterApplicationRequest";
import { getOptionLabel, getOptionValue } from "common/enums";
import OtherApplicationViewModal from "Components/OtherApplicationViewModal";
import BreadCrumb from "Components/Common/BreadCrumb";
import Tooltip from "Components/Common/Tooltip";
import OtherApplicationActions from "./OtherApplicationActions";
import { applicationStatusTypes } from "common/constant";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { formatUtilityAddress } from "utils/formatUtilityAddress";

import {
  APPLICATION_COMMENT,
  APPLICATION_DETAILS,
  TECHNICAL_SANCTION,
} from "tagTypes";

const RECORDS_PER_PAGE = 10;

const OtherApplicationRequest = ({ t }) => {
  const { utilityRoles } = useSelector((state) => state.Login.user);

  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [applicationType, setApplicationType] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const applicationId =
    searchParams.get("applicationId") || searchParams.get("groupId");
  const [query, setQuery] = useState({
    utilities: utilityRoles.map(({ utilityId }) => utilityId || ""),
  });

  const lg = useMediaQuery("(min-width: 900px) and (max-width: 1199px)");
  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const viewApplicationBtnHandler = (
    applicationId,
    applicationType,
    applicationStatus
  ) => {
    searchParams.set(
      applicationStatus === "saved" ? "groupId" : "applicationId",
      applicationId
    );
    setSearchParams(searchParams);
    setApplicationType(applicationType);
  };
  const columns = [
    {
      title: "Application Number",
      field: "applicationNumber",
    },
    {
      title: "Application Type",
      field: "applicationType.name",
    },
    {
      title: "Utility",
      renderCell: ({ utilities }) =>
        utilities
          .map((val) => getOptionLabel("utilityOptions", val))
          .join(", "),
    },
    {
      title: "Name",
      field: "name",
    },
    {
      title: "Consumer Number",
      field: "consumerNumber",
    },
    {
      title: "Utility Address",
      renderCell: ({ utilityAddress }) => {
        const address = formatUtilityAddress(utilityAddress);

        if (address) {
          return (
            <>
              <p className="m-0">{address}, Gandhinagar-382050</p>
            </>
          );
        }

        return "--";
      },
      columnProps: { style: { width: "350px", minWidth: "350px" } },
    },
    {
      title: "Date",
      renderCell: (item) =>
        moment(item.submissionDate).isValid()
          ? convertUTCDateToLocalDate(item.submissionDate).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Status",
      renderCell: ({
        applicationStatus: { name, id } = {},
        isAllPendingDocumentsApproved,
        isTechnicalFeasibility,
        utilities,
        isCommented=false
      }) => {
        const isDcsUtility = utilities.some(
          (value) => value === getOptionValue("utilityOptions","DCS")
        );
        return (
          <>
            {(isDcsUtility&&isCommented)?"Commented":name}
            {id === applicationStatusTypes["Estimate deposited"] &&
              !isAllPendingDocumentsApproved && (
                <Tooltip
                  title={t("There are some documents to upload/approve.")}
                >
                  <InfoOutlinedIcon
                    className="ms-1 cursor-pointer"
                    color="primary"
                    fontSize="small"
                  />
                </Tooltip>
              )}
            {id === applicationStatusTypes["Rejected"] &&
              !isTechnicalFeasibility && (
                <Tooltip
                  title={t(
                    "Location is not technically feasible for this application."
                  )}
                >
                  <InfoOutlinedIcon
                    className="ms-1 cursor-pointer"
                    color="primary"
                    fontSize="small"
                  />
                </Tooltip>
              )}
          </>
        );
      },
    },
    {
      title: "Actions",
      renderCell: (item) => (
        <OtherApplicationActions
          item={item}
          viewApplicationBtnHandler={viewApplicationBtnHandler}
        />
      ),
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/other-utility-application",
      tags: [APPLICATION_DETAILS, APPLICATION_COMMENT, TECHNICAL_SANCTION],
      query,
    }),
    [query]
  );

  return (
    <div className="page-content h-100">
      <BreadCrumb title={t("Application Request Details")} />

      <FilterApplicationRequests
        setQuery={setQuery}
        resetPage={handleResetPage}
      />

      {!!applicationId && (
        <OtherApplicationViewModal
          viewApplicationQuery={{
            [searchParams.has("groupId") ? "groupId" : "applicationId"]:
              applicationId,
          }}
          applicationType={applicationType}
          handleClose={() => {
            searchParams.delete(
              searchParams.has("groupId") ? "groupId" : "applicationId"
            );
            setSearchParams(searchParams);
          }}
        />
      )}
      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 430px)"
            : md
            ? "calc(100vh - 400px)"
            : lg
            ? "calc(100vh - 340px)"
            : "calc(100vh - 300px)"
        }
      />
    </div>
  );
};

OtherApplicationRequest.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OtherApplicationRequest);
